import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationPanelService } from '@core/services';
import { Drawer } from '@shared/abstraction/drawer/drawer';
import { DrawerBody } from '@shared/abstraction/drawer/drawer-body';
import { DrawerComponent } from '@shared/components/drawer/drawer.component';
import { NotificationPanelResponse } from '@shared/models/notification-panel/notification-panel-response';
import { NotificationPanelSecurity } from '@shared/models/notification-panel/notification-panel-security';
import { CurrentUserService } from '@shared/services';
import { filter } from 'rxjs';

@Component({
  standalone: false,
  selector: 'gstar-notification-panel',
  templateUrl: './notification-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationPanelComponent implements DrawerBody, OnInit {
  private readonly notificationService = inject(NotificationPanelService);
  private readonly currentUserService = inject(CurrentUserService);

  @Input() setting!: Drawer;
  @Input() drawerPanel!: DrawerComponent;

  pendingApprovedNotifications: WritableSignal<NotificationPanelSecurity[]> =
    signal([]);
  pendingApprovedCounter: WritableSignal<string> = signal('');
  approvedNotifications: WritableSignal<NotificationPanelSecurity[]> = signal(
    []
  );
  approvedCounter: WritableSignal<string> = signal('');

  isAdmin: Signal<boolean> = this.currentUserService.isAdmin;

  destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.notificationService.pendingApprovalSecuritiesPanel$
      .pipe(
        filter(() => this.isAdmin()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ count, securities }) => {
        this.pendingApprovedCounter.set(
          this.notificationService.getNotificationCountString(count)
        );
        this.pendingApprovedNotifications.set(securities);
      });

    this.notificationService.approvedSecuritiesPanel$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ count, securities }) => {
        this.approvedCounter.set(
          this.notificationService.getNotificationCountString(count)
        );
        this.approvedNotifications.set(securities);
      });
  }

  closeDrawer(notificationResponse: NotificationPanelResponse): void {
    this.drawerPanel.settings?.fromBody().next(notificationResponse);
    this.drawerPanel.close();
  }

  removeApprovedNotification(): void {
    const approvedIds: number[] = this.approvedNotifications().map(
      (notification: NotificationPanelSecurity) => notification.securityId
    );

    this.notificationService
      .notifyApprovedSecurity(approvedIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
